import { useEffect, useState } from 'react'
import { CalendarDays, FileText, MapPin, Stethoscope, User, X } from 'lucide-react'
import {
  getPatientDocument,
  getPatientEncounter,
  listPatientDocuments,
  listPatientEncounters,
  type DocumentReferenceView,
  type EncounterView,
} from '../api/clinical'
import { useLang, vocab, t, fmt } from '../lib/i18n'
import { SAMPLE_ENCOUNTERS } from '../lib/samples/encounters'
import { SAMPLE_DOCUMENTS } from '../lib/samples/documents'

/* ============================================================
   Encounters + documents — live FHIR-aligned chart sections.

   Rows come from GET /api/v1/patients/{id}/encounters and
   /api/v1/patients/{id}/documents (EncounterView /
   DocumentReferenceView — see docs/design/provider-backend-api-2026-08-30.md
   and ADR-015). Clicking a row opens a detail panel fed by the single-item
   endpoint. Loading and error states are honest — no fabricated live rows.

   Demo fallback (deployed site: pages.dev has no reachable backend): when
   the list fetch fails OR returns zero rows, the section renders the bundled
   contract-exact samples (src/lib/samples/) with a visible "Sample data"
   badge (prov.sampleData, EN/AR) so demo rows are never mistaken for live
   records. Click-through detail panels work identically on samples — the
   panel renders the sample object directly instead of fetching.
   ============================================================ */

const ENCOUNTER_TYPE_TONE: Record<string, string> = {
  emergency: 'bg-rose-50 text-rose-800 border-rose-200',
  outpatient: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  inpatient: 'bg-sky-50 text-sky-800 border-sky-200',
  telehealth: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  home: 'bg-amber-50 text-amber-800 border-amber-200',
}

const DOC_TYPE_TONE: Record<string, string> = {
  'discharge-summary': 'bg-sky-50 text-sky-800 border-sky-200',
  'lab-report': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'imaging-report': 'bg-indigo-50 text-indigo-800 border-indigo-200',
  'referral-letter': 'bg-amber-50 text-amber-800 border-amber-200',
  'scanned-paper': 'bg-stone-100 text-stone-700 border-stone-200',
  consent: 'bg-rose-50 text-rose-800 border-rose-200',
  insurance: 'bg-stone-100 text-stone-700 border-stone-200',
  other: 'bg-stone-100 text-stone-700 border-stone-200',
}

/** Humanize a snake/kebab token when no dictionary label exists. */
function humanizeToken(value: string) {
  return value
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Localized label for an encounter class/type token (`enc.type.*` first, then vocab, then humanized). */
function encounterTypeLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase()
  const typed = t(`enc.type.${key}`)
  if (typed !== `enc.type.${key}`) return typed
  const viaVocab = vocab(key)
  return viaVocab !== key ? viaVocab : humanizeToken(key)
}

/** Localized label for a FHIR Encounter status token (`enc.status.*`). */
function encounterStatusLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase()
  const translated = t(`enc.status.${key}`)
  return translated !== `enc.status.${key}` ? translated : humanizeToken(key)
}

/** Localized label for a discharge-disposition token (`enc.disposition.*`). */
function dispositionLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase().replace(/\s+/g, '-')
  const translated = t(`enc.disposition.${key}`)
  return translated !== `enc.disposition.${key}` ? translated : humanizeToken(key)
}

/** Localized label for a note type/source token (`enc.noteType.*` / `enc.noteSource.*` / `enc.signed.*`). */
function noteTokenLabel(ns: 'noteType' | 'noteSource' | 'signed', value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase().replace(/\s+/g, '-')
  const translated = t(`enc.${ns}.${key}`)
  return translated !== `enc.${ns}.${key}` ? translated : humanizeToken(key)
}

/** Localized label for a document type token (`doc.type.*` first, then vocab, then humanized). */
function docTypeLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase()
  const typed = t(`doc.type.${key}`)
  if (typed !== `doc.type.${key}`) return typed
  const viaVocab = vocab(key)
  return viaVocab !== key ? viaVocab : humanizeToken(key)
}

/** Localized label for a document retention-class token (`doc.retention.*`). */
function retentionLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase().replace(/[\s_]+/g, '-')
  const translated = t(`doc.retention.${key}`)
  return translated !== `doc.retention.${key}` ? translated : humanizeToken(key)
}

/** Localized label for a document content-status token (`doc.contentStatus.*`). */
function contentStatusLabel(value: string) {
  if (!value) return '—'
  const key = value.trim().toLowerCase().replace(/[\s_]+/g, '-')
  const translated = t(`doc.contentStatus.${key}`)
  return translated !== `doc.contentStatus.${key}` ? translated : humanizeToken(key)
}

/** Short human label for a MIME type (pdf/image/text) with the raw value kept in the detail panel. */
function mimeShortLabel(mimeType: string) {
  if (mimeType === 'application/pdf') return t('doc.pdf')
  if (mimeType.startsWith('image/')) return t('doc.image')
  if (mimeType.startsWith('text/')) return t('doc.text')
  return mimeType
}

/** Render an ISO timestamp as a local date-time line; dates stay ISO (clinical precision). */
function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  // Date-only rows (period_start 'YYYY-MM-DD') render as-is to avoid TZ shifts.
  if (iso.length <= 10) return iso
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`
}

function formatBytes(size: number | null) {
  if (size === null || size === undefined || Number.isNaN(size)) return '—'
  if (size < 1024) return fmt(t('doc.sizeB'), { n: size })
  if (size < 1024 * 1024) return fmt(t('doc.sizeKB'), { n: (size / 1024).toFixed(1) })
  return fmt(t('doc.sizeMB'), { n: (size / (1024 * 1024)).toFixed(1) })
}

/** Encounter period as one clinical line (start → end, or start only). */
function periodLine(start: string | null, end: string | null) {
  if (!start && !end) return '—'
  if (start && end) return `${formatDateTime(start)} → ${formatDateTime(end)}`
  return formatDateTime(start ?? end)
}

/** Encounter location line — clinic ward combined with the FHIR location slots. */
function locationLine(encounter: Pick<EncounterView, 'location_ward' | 'location_clinic'>) {
  const parts = [encounter.location_clinic, encounter.location_ward].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/* =============================== Lists =============================== */

type ListState<T> =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; items: T[]; sampled: boolean }

/** Small honesty badge shown whenever a section is rendering bundled samples. */
function SampleBadge() {
  return (
    <span
      className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
      data-testid="sample-data-badge"
    >
      {t('prov.sampleData')}
    </span>
  )
}

export function RecentEncounters({
  patientId,
  refreshKey = 0,
  focusId = null,
}: {
  patientId: string
  /** Bump to force a live refetch (e.g. after the scribe dock commits). */
  refreshKey?: number
  /** Auto-open this encounter's detail panel (scribe commit jump-to). */
  focusId?: string | null
}) {
  useLang()
  const [state, setState] = useState<ListState<EncounterView>>({ phase: 'loading' })
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (focusId) setSelectedId(focusId)
  }, [focusId])

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    listPatientEncounters(patientId, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return
        // Contract-exact samples when the live backend is unreachable or the
        // patient has no rows yet — keeps the deployed demo clickable.
        if (res.items.length === 0) setState({ phase: 'ready', items: SAMPLE_ENCOUNTERS, sampled: true })
        else setState({ phase: 'ready', items: res.items, sampled: false })
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ phase: 'ready', items: SAMPLE_ENCOUNTERS, sampled: true })
      })
    return () => controller.abort()
  }, [patientId, refreshKey])

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft"
      aria-label={t('enc.aria')}
      data-testid="encounters-section"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-primary-700" />
          <h2 className="text-lg font-semibold text-stone-950">{t('enc.title')}</h2>
        </div>
        {state.phase === 'ready' && !state.sampled && (
          <span className="text-xs text-stone-500">{fmt(t('enc.nVisits'), { n: state.items.length })}</span>
        )}
        {state.phase === 'ready' && state.sampled && <SampleBadge />}
      </div>

      {state.phase === 'loading' && (
        <div className="space-y-3" role="status" aria-label={t('enc.loading')}>
          <div className="skeleton h-12" />
          <div className="skeleton h-12" />
          <div className="skeleton h-12" />
        </div>
      )}

      {state.phase === 'error' && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" role="alert">
          {t('enc.loadFailed')}
        </p>
      )}

      {state.phase === 'ready' && state.items.length === 0 && (
        <p className="px-1 py-6 text-center text-sm text-stone-500">{t('enc.empty')}</p>
      )}

      {state.phase === 'ready' && state.items.length > 0 && (
        <ul className="divide-y divide-stone-100">
          {state.items.map((encounter) => (
            <li key={encounter.id}>
              <button
                type="button"
                onClick={() => setSelectedId(encounter.id)}
                aria-label={fmt(t('enc.openAria'), {
                  reason: encounter.reason_text || encounterTypeLabel(encounter.type),
                })}
                className="group flex w-full items-start gap-3 rounded-lg px-2 py-3 text-start transition hover:bg-emerald-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 active:scale-[0.99]"
              >
                <span
                  className={`mt-0.5 inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                    ENCOUNTER_TYPE_TONE[encounter.type] ?? ENCOUNTER_TYPE_TONE.outpatient
                  }`}
                >
                  {encounterTypeLabel(encounter.type)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-stone-900 group-hover:text-emerald-900">
                    {encounter.reason_text || encounterTypeLabel(encounter.type)}
                    {encounter.reason_code ? (
                      <span
                        className="ltr-keep ms-2 inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-1.5 py-px font-mono text-[11px] font-semibold text-sky-800"
                        data-testid={`encounter-icd10-${encounter.reason_code}`}
                        title={t('enc.icd10ChipAria')}
                      >
                        {encounter.reason_code}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5 text-stone-500">
                    {locationLine(encounter) ?? '—'}
                  </span>
                </span>
                <span className="shrink-0 text-end">
                  <span className="block text-xs font-medium text-stone-600">
                    {formatDateTime(encounter.period_start)}
                  </span>
                  {encounter.discharge_disposition ? (
                    <span className="mt-0.5 block text-[11px] text-stone-400">
                      → {dispositionLabel(encounter.discharge_disposition)}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedId && (
        <EncounterDetailPanel
          patientId={patientId}
          encounterId={selectedId}
          samples={state.phase === 'ready' && state.sampled ? state.items : undefined}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  )
}

export function Documents({ patientId }: { patientId: string }) {
  useLang()
  const [state, setState] = useState<ListState<DocumentReferenceView>>({ phase: 'loading' })
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    listPatientDocuments(patientId, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return
        if (res.items.length === 0) setState({ phase: 'ready', items: SAMPLE_DOCUMENTS, sampled: true })
        else setState({ phase: 'ready', items: res.items, sampled: false })
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ phase: 'ready', items: SAMPLE_DOCUMENTS, sampled: true })
      })
    return () => controller.abort()
  }, [patientId])

  return (
    <section
      className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft"
      aria-label={t('doc.aria')}
      data-testid="documents-section"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-700" />
          <h2 className="text-lg font-semibold text-stone-950">{t('doc.title')}</h2>
        </div>
        {state.phase === 'ready' && !state.sampled && (
          <span className="text-xs text-stone-500">{fmt(t('doc.nFiles'), { n: state.items.length })}</span>
        )}
        {state.phase === 'ready' && state.sampled && <SampleBadge />}
      </div>

      {state.phase === 'loading' && (
        <div className="space-y-3" role="status" aria-label={t('doc.loading')}>
          <div className="skeleton h-12" />
          <div className="skeleton h-12" />
          <div className="skeleton h-12" />
        </div>
      )}

      {state.phase === 'error' && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" role="alert">
          {t('doc.loadFailed')}
        </p>
      )}

      {state.phase === 'ready' && state.items.length === 0 && (
        <p className="px-1 py-6 text-center text-sm text-stone-500">{t('doc.empty')}</p>
      )}

      {state.phase === 'ready' && state.items.length > 0 && (
        <ul className="divide-y divide-stone-100">
          {state.items.map((document) => (
            <li key={document.id}>
              <button
                type="button"
                onClick={() => setSelectedId(document.id)}
                aria-label={fmt(t('doc.openAria'), { title: document.title })}
                className="group flex w-full items-start gap-3 rounded-lg px-2 py-3 text-start transition hover:bg-emerald-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 active:scale-[0.99]"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-stone-100 text-stone-500 transition group-hover:bg-emerald-100 group-hover:text-emerald-800">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-stone-900 group-hover:text-emerald-900">
                    {document.title}
                  </span>
                  <span className="mt-1 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
                        DOC_TYPE_TONE[document.doc_type] ?? DOC_TYPE_TONE.other
                      }`}
                    >
                      {docTypeLabel(document.doc_type)}
                    </span>
                    <span className="text-[11px] text-stone-400">{formatDateTime(document.uploaded_at)}</span>
                  </span>
                </span>
                <span className="shrink-0 text-[11px] font-medium text-stone-400">
                  {mimeShortLabel(document.mime_type)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedId && (
        <DocumentDetailPanel
          patientId={patientId}
          documentId={selectedId}
          samples={state.phase === 'ready' && state.sampled ? state.items : undefined}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  )
}

/* ============================ Detail panels ============================ */

function DetailShell({
  titleId,
  title,
  onClose,
  children,
}: {
  titleId: string
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-stone-950/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl animate-pop-in overflow-y-auto rounded-2xl border border-stone-200 bg-white p-6 shadow-lift-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <h3 id={titleId} className="min-w-0 flex-1 text-lg font-semibold text-stone-950">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('panel.close')}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function PanelLoading() {
  return (
    <div className="space-y-3" role="status" aria-label={t('panel.loading')}>
      <div className="skeleton h-6 w-2/3" />
      <div className="skeleton h-20" />
      <div className="skeleton h-20" />
    </div>
  )
}

function PanelError() {
  return (
    <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800" role="alert">
      {t('panel.loadFailed')}
    </p>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_1fr] sm:gap-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</dt>
      <dd className="min-w-0 text-sm leading-6 text-stone-900">{children}</dd>
    </div>
  )
}

function EncounterDetailPanel({
  patientId,
  encounterId,
  samples,
  onClose,
}: {
  patientId: string
  encounterId: string
  /** Bundle-sample mode: render the matching sample directly, no fetch. */
  samples?: EncounterView[]
  onClose: () => void
}) {
  useLang()
  const [state, setState] = useState<ListState<EncounterView>>({ phase: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    if (samples) {
      const sample = samples.find((item) => item.id === encounterId)
      setState(
        sample
          ? { phase: 'ready', items: [sample], sampled: true }
          : { phase: 'error' },
      )
      return () => controller.abort()
    }
    getPatientEncounter(patientId, encounterId, { signal: controller.signal })
      .then((encounter) => {
        if (!controller.signal.aborted) setState({ phase: 'ready', items: [encounter], sampled: false })
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ phase: 'error' })
      })
    return () => controller.abort()
  }, [patientId, encounterId, samples])

  const encounter = state.phase === 'ready' ? state.items[0] : null

  return (
    <DetailShell
      titleId="encounter-panel-title"
      title={encounter ? encounter.reason_text || encounterTypeLabel(encounter.type) : t('enc.panelTitle')}
      onClose={onClose}
    >
      {state.phase === 'loading' && <PanelLoading />}
      {state.phase === 'error' && <PanelError />}
      {encounter && (
        <div className="space-y-5">
          <dl className="space-y-3">
            <DetailRow label={t('enc.field.type')}>
              <span
                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                  ENCOUNTER_TYPE_TONE[encounter.type] ?? ENCOUNTER_TYPE_TONE.outpatient
                }`}
              >
                {encounterTypeLabel(encounter.type)}
              </span>
            </DetailRow>
            <DetailRow label={t('enc.field.status')}>{encounterStatusLabel(encounter.status)}</DetailRow>
            <DetailRow label={t('enc.field.period')}>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-stone-400" />
                {periodLine(encounter.period_start, encounter.period_end)}
              </span>
            </DetailRow>
            <DetailRow label={t('enc.field.reason')}>
              <span dir="auto">
                {encounter.reason_text || '—'}
                {encounter.reason_code ? (
                  <span className="ltr-keep ms-2 font-mono text-xs text-stone-500">[{encounter.reason_code}]</span>
                ) : null}
              </span>
            </DetailRow>
            <DetailRow label={t('enc.field.practitioner')}>
              <span className="inline-flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-stone-400" />
                <span dir="auto">{encounter.practitioner_name || '—'}</span>
              </span>
            </DetailRow>
            <DetailRow label={t('enc.field.location')}>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-stone-400" />
                <span dir="auto">{locationLine(encounter) ?? '—'}</span>
              </span>
            </DetailRow>
            <DetailRow label={t('enc.field.disposition')}>
              {encounter.discharge_disposition ? dispositionLabel(encounter.discharge_disposition) : '—'}
            </DetailRow>
          </dl>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-stone-950">
              {t('enc.notesHeading')}
              <span className="ms-2 rounded-md bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-600">
                {encounter.notes.length}
              </span>
            </h4>
            {encounter.notes.length === 0 ? (
              <p className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-500">
                {t('enc.noNotes')}
              </p>
            ) : (
              <ul className="space-y-3">
                {encounter.notes.map((note, index) => (
                  <li key={index} className="rounded-lg border border-stone-200 bg-stone-50/60 p-4">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                        {noteTokenLabel('noteType', note.note_type)}
                      </span>
                      <span className="inline-flex items-center rounded-md border border-stone-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                        {noteTokenLabel('noteSource', note.source)}
                      </span>
                      {note.author && (
                        <span className="text-[11px] font-medium text-stone-500" dir="auto">
                          {note.author}
                        </span>
                      )}
                      {note.taken_at && (
                        <span className="ltr-keep text-[11px] text-stone-400">{formatDateTime(note.taken_at)}</span>
                      )}
                      {note.signed_status && (
                        <span className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                          {noteTokenLabel('signed', note.signed_status)}
                        </span>
                      )}
                    </div>
                    {note.title && (
                      <div className="mb-1 text-sm font-semibold text-stone-900" dir="auto">
                        {note.title}
                      </div>
                    )}
                    <p className="whitespace-pre-line text-sm leading-6 text-stone-700" dir="auto">
                      {note.body}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </DetailShell>
  )
}

function DocumentDetailPanel({
  patientId,
  documentId,
  samples,
  onClose,
}: {
  patientId: string
  documentId: string
  /** Bundle-sample mode: render the matching sample directly, no fetch. */
  samples?: DocumentReferenceView[]
  onClose: () => void
}) {
  useLang()
  const [state, setState] = useState<ListState<DocumentReferenceView>>({ phase: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    setState({ phase: 'loading' })
    if (samples) {
      const sample = samples.find((item) => item.id === documentId)
      setState(
        sample
          ? { phase: 'ready', items: [sample], sampled: true }
          : { phase: 'error' },
      )
      return () => controller.abort()
    }
    getPatientDocument(patientId, documentId, { signal: controller.signal })
      .then((document) => {
        if (!controller.signal.aborted) setState({ phase: 'ready', items: [document], sampled: false })
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ phase: 'error' })
      })
    return () => controller.abort()
  }, [patientId, documentId, samples])

  const document = state.phase === 'ready' ? state.items[0] : null

  return (
    <DetailShell
      titleId="document-panel-title"
      title={document ? document.title : t('doc.panelTitle')}
      onClose={onClose}
    >
      {state.phase === 'loading' && <PanelLoading />}
      {state.phase === 'error' && <PanelError />}
      {document && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50/60 p-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-white text-stone-500 ring-1 ring-stone-200">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <span
                className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                  DOC_TYPE_TONE[document.doc_type] ?? DOC_TYPE_TONE.other
                }`}
              >
                {docTypeLabel(document.doc_type)}
              </span>
              <div className="ltr-keep mt-1 truncate font-mono text-xs text-stone-500">{document.filename}</div>
            </div>
          </div>

          {document.description && (
            <p className="rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm leading-6 text-stone-700" dir="auto">
              {document.description}
            </p>
          )}

          <dl className="space-y-3">
            <DetailRow label={t('doc.field.filename')}>
              <span className="ltr-keep font-mono text-xs">{document.filename}</span>
            </DetailRow>
            <DetailRow label={t('doc.field.mime')}>
              <span className="ltr-keep font-mono text-xs">{document.mime_type}</span>
              <span className="ms-2 text-xs text-stone-500">({mimeShortLabel(document.mime_type)})</span>
            </DetailRow>
            <DetailRow label={t('doc.field.size')}>{formatBytes(document.size_bytes)}</DetailRow>
            <DetailRow label={t('doc.field.sha256')}>
              {document.sha256 ? (
                <span className="ltr-keep break-all font-mono text-xs text-stone-600">{document.sha256}</span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label={t('doc.field.uploadedBy')}>
              <span dir="auto">{document.uploaded_by || '—'}</span>
            </DetailRow>
            <DetailRow label={t('doc.field.uploadedAt')}>
              <span className="ltr-keep">{formatDateTime(document.uploaded_at)}</span>
            </DetailRow>
            <DetailRow label={t('doc.field.retention')}>
              {document.retention_class ? retentionLabel(document.retention_class) : '—'}
            </DetailRow>
            <DetailRow label={t('doc.field.contentStatus')}>
              {document.content_status ? (
                <span
                  className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                    document.content_status === 'available'
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-amber-50 text-amber-800 border-amber-200'
                  }`}
                >
                  {contentStatusLabel(document.content_status)}
                </span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label={t('doc.field.encounter')}>
              {document.encounter_id ? (
                <span className="ltr-keep font-mono text-xs">{document.encounter_id}</span>
              ) : (
                t('doc.noEncounter')
              )}
            </DetailRow>
          </dl>

          <p className="text-xs leading-5 text-stone-400">{t('doc.bodyFollowup')}</p>
        </div>
      )}
    </DetailShell>
  )
}
